import { t } from 'i18next';
import { isNil } from 'lodash';
import { Field } from '@shared/data/entity/fields/Field';
import {
  getComponentLabelWithFallback,
  isDateComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

/**
 * True when a card form field matches the search: its label, placeholder, type or one of the
 * type's aliases, or the value entered in it. Mirrors the field search in the builders, plus
 * the value, which only a card has.
 */
export function matchesFieldSearch(
  component: API.AnyComponent,
  field: Field | null | undefined,
  query: string,
): boolean {
  const searchQuery = query.trim().toLowerCase();
  if (searchQuery === '') {
    return true;
  }

  const placeholder = 'placeholder' in component ? component.placeholder : null;
  const value = isNil(field) || field.isEmpty ? '' : field.toDisplayString();
  const candidates = [
    getComponentLabelWithFallback(component),
    typeof placeholder === 'string' ? placeholder : '',
    component.type,
    value,
    ...getComponentTypeAliases(component),
  ];

  return candidates.some(candidate => candidate.toLowerCase().includes(searchQuery));
}

function getComponentTypeAliases(component: API.AnyComponent): string[] {
  if (isTagComponent(component)) {
    if (component.multiselect) {
      return [
        t('b.fields.fieldContent.searchAliases.multiSelect'),
        t('b.fields.fieldContent.searchAliases.multiselect'),
        t('b.fields.fieldContent.searchAliases.multipleSelection'),
      ];
    }
    return [
      t('b.fields.fieldContent.searchAliases.singleSelect'),
      t('b.fields.fieldContent.searchAliases.singleSelectSpaced'),
    ];
  }

  if (isDateComponent(component)) {
    return [
      t('b.fields.fieldContent.searchAliases.datePicker'),
      t('b.fields.fieldContent.searchAliases.date'),
    ];
  }

  if (isTextComponent(component)) {
    if (component.multiline) {
      return [
        t('b.fields.fieldContent.searchAliases.longText'),
        t('b.fields.fieldContent.searchAliases.textarea'),
        t('b.fields.fieldContent.searchAliases.paragraph'),
      ];
    }
    return [
      t('b.fields.fieldContent.searchAliases.shortText'),
      t('b.fields.fieldContent.searchAliases.textInput'),
    ];
  }

  const staticAliases: Record<string, string[]> = {
    URL: [
      t('b.fields.fieldContent.searchAliases.link'),
      t('b.fields.fieldContent.searchAliases.web'),
      t('b.fields.fieldContent.searchAliases.website'),
    ],
    EMAIL: [
      t('b.fields.fieldContent.searchAliases.email'),
      t('b.fields.fieldContent.searchAliases.emailAddress'),
    ],
    FILE: [
      t('b.fields.fieldContent.searchAliases.file'),
      t('b.fields.fieldContent.searchAliases.fileUpload'),
      t('b.fields.fieldContent.searchAliases.imageUpload'),
      t('b.fields.fieldContent.searchAliases.attachment'),
    ],
    GEOLOCATION: [
      t('b.fields.fieldContent.searchAliases.location'),
      t('b.fields.fieldContent.searchAliases.geolocation'),
      t('b.fields.fieldContent.searchAliases.map'),
    ],
    NUMBER: [
      t('b.fields.fieldContent.searchAliases.number'),
      t('b.fields.fieldContent.searchAliases.numeric'),
      t('b.fields.fieldContent.searchAliases.integer'),
    ],
    PHONE: [
      t('b.fields.fieldContent.searchAliases.phone'),
      t('b.fields.fieldContent.searchAliases.telephone'),
      t('b.fields.fieldContent.searchAliases.mobile'),
    ],
    ADDRESS: [
      t('b.fields.fieldContent.searchAliases.address'),
      t('b.fields.fieldContent.searchAliases.location'),
      t('b.fields.fieldContent.searchAliases.street'),
    ],
    PERSON: [
      t('b.fields.fieldContent.searchAliases.person'),
      t('b.fields.fieldContent.searchAliases.user'),
      t('b.fields.fieldContent.searchAliases.contact'),
    ],
  };

  return staticAliases[component.type] ?? [];
}
